import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, Container, FormControlLabel, Paper, Stack, Typography } from '@mui/material';
import { emailMarketing } from '../../services/emailMarketingApi';

export default function EmailPreferences({ compact = false }) {
  const [consent, setConsent] = useState(false);
  const [saved, setSaved] = useState(false);
  const [suppressed, setSuppressed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let active = true;
    emailMarketing('getPreference').then((result) => {
      if (!active) return;
      setConsent(result.consent);
      setSuppressed(result.suppressed);
      setAvailable(true);
    }).catch(() => { if (active) setError('Email preferences are temporarily unavailable. Please refresh to try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const updateFromReminder = (event) => {
      setConsent(event.detail?.consent === true);
      setAvailable(true);
      setSaved(true);
    };
    window.addEventListener('marketing-preference-changed', updateFromReminder);
    return () => window.removeEventListener('marketing-preference-changed', updateFromReminder);
  }, []);

  const save = async () => {
    setError(''); setSaving(true); setSaved(false);
    try {
      const result = await emailMarketing('setPreference', { consent });
      setConsent(result.consent); setSaved(true);
      window.dispatchEvent(new CustomEvent('marketing-preference-changed', { detail: { consent: result.consent === true } }));
    } catch (saveError) { setError('Could not save your email preference. Please try again.'); }
    finally { setSaving(false); }
  };

  const content = <Stack spacing={compact ? 1.5 : 2}>
      <Typography variant="overline" color="success.main">{compact ? 'Stay in the rhythm' : 'Your account'}</Typography>
      <Typography variant="h5" component="h2">{compact ? 'A little Dancehall in your inbox.' : 'Email preferences'}</Typography>
      <Typography color="text.secondary">{compact ? 'Dance tips, new classes, and membership offers. Unsubscribe anytime. Your choice does not affect account or billing emails.' : 'Choose whether JA Legends Dance may send you dance tips, class updates, and membership offers. Your choice does not affect important account or billing emails.'}</Typography>
      {loading ? <CircularProgress size={24} /> : <>
        <FormControlLabel sx={{ alignItems: 'flex-start', ml: -1 }} control={<Checkbox checked={Boolean(consent)} disabled={saving || suppressed || !available} onChange={(event) => { setConsent(event.target.checked); setSaved(false); }} />} label={<Typography sx={{ pt: 1 }}>Yes, send me JA Legends Dance marketing emails.</Typography>} />
        {suppressed && <Alert severity="warning">This email address is suppressed after a delivery problem. Contact support if you believe this is an error.</Alert>}
        <Button variant="contained" color="success" onClick={save} disabled={saving || suppressed || !available} sx={{ alignSelf: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>{saving ? 'Saving…' : 'Save preference'}</Button>
        {saved && <Alert severity="success">Your email preference has been saved.</Alert>}
      </>}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>;

  if (compact) return <Box component="section" className="footer-email-preferences">{content}</Box>;
  return <Box component="section" id="email-preferences" sx={{ py: 5 }}><Container maxWidth="lg"><Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}>{content}</Paper></Container></Box>;
}
